/**
 * Passphrase Entry Modal
 *
 * Modal for re-entering passphrase to unlock encryption in a new session.
 * Shown when user has encryption enabled but key is not in memory.
 *
 * Features:
 * - Simple passphrase input
 * - Show/hide toggle
 * - Error handling for incorrect passphrase
 * - Optional "Remember this session" (keeps key in memory)
 * - Cannot be dismissed (blocks UI until unlocked)
 *
 * @fileoverview Passphrase entry component for session unlock
 */

import { Eye, EyeOff, Lock, Shield } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface PassphraseEntryProps {
  onSuccess: (passphrase: string) => Promise<void>;
  onForgot?: () => void;
  onSignOut?: () => void;
  title?: string;
  description?: string;
  allowCancel?: boolean;
  onCancel?: () => void;
}

export function PassphraseEntry({
  onSuccess,
  onForgot,
  onSignOut,
  title = 'Enter Your Passphrase',
  description = 'Unlock your encrypted case data',
  allowCancel = false,
  onCancel,
}: PassphraseEntryProps) {
  const [passphrase, setPassphrase] = useState('');
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Auto-focus input on mount
  useEffect(() => {
    const input = document.getElementById('passphrase-input');
    if (input) {
      input.focus();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passphrase) {
      setError('Passphrase is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSuccess(passphrase);
      // If successful, modal will be closed by parent
    } catch (error) {
      setAttemptCount((prev) => prev + 1);

      // Provide helpful error messages
      const errorMessage =
        error instanceof Error ? error.message : 'Incorrect passphrase. Please try again.';

      setError(errorMessage);
      toast.error(errorMessage);

      // Clear passphrase field on error
      setPassphrase('');

      // Warn after multiple failed attempts
      if (attemptCount >= 2) {
        toast.error(
          'Multiple failed attempts. Please ensure you are using the correct passphrase.',
          { duration: 5000 },
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Clear error on typing
    if (error) {
      setError(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full"
        style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 0, 0, 0.1)' }}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#EAE0CB] rounded-lg">
              <Lock className="h-6 w-6 text-[#A66B32]" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          </div>
          <p className="text-sm text-gray-600">{description}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Passphrase Input */}
          <div>
            <Label htmlFor="passphrase-input" className="text-gray-700 font-medium">
              Passphrase
            </Label>
            <div className="relative mt-1">
              <Input
                id="passphrase-input"
                type={showPassphrase ? 'text' : 'password'}
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`pr-12 bg-white text-gray-900 focus:border-[#A66B32] focus:ring-[#A66B32]/30/20 ${
                  error ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your passphrase"
                disabled={isSubmitting}
                autoComplete="current-password"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassphrase(!showPassphrase)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#A66B32]/30 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                tabIndex={-1}
                disabled={isSubmitting}
                aria-label={showPassphrase ? 'Hide passphrase' : 'Show passphrase'}
              >
                {showPassphrase ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <span className="font-medium">Error:</span> {error}
              </p>
            )}

            {/* Attempt Warning */}
            {attemptCount > 0 && !error && (
              <p className="mt-2 text-xs text-gray-600">Failed attempts: {attemptCount}</p>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-[#EAE0CB]/40 border border-[#3A2718]/15 rounded-lg p-4">
            <div className="flex gap-3">
              <Shield className="h-5 w-5 text-[#A66B32] flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <h3 className="font-semibold text-[#241610] text-sm">Why do I need this?</h3>
                <p className="text-xs text-[#241610]">
                  Your passphrase decrypts your case data locally in your browser. We don't store it
                  on our servers, so you need to enter it each session.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              type="submit"
              disabled={isSubmitting || !passphrase}
              className="w-full bg-[#A66B32] text-white hover:bg-[#241610] disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2 justify-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Unlocking...
                </div>
              ) : (
                'Unlock Encryption'
              )}
            </Button>

            {/* Secondary Actions */}
            <div className="flex gap-3">
              {onForgot && (
                <button
                  type="button"
                  onClick={onForgot}
                  className="flex-1 text-sm text-[#A66B32] hover:text-[#3A2718] font-medium disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  Forgot Passphrase?
                </button>
              )}

              {allowCancel && onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 text-sm text-gray-600 hover:text-gray-700 font-medium disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              )}
              {onSignOut && (
                <button
                  type="button"
                  onClick={onSignOut}
                  className="flex-1 text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  Sign Out
                </button>
              )}
            </div>
          </div>

          {/* Help Text */}
          <p className="text-xs text-gray-500 text-center">
            Press Enter to unlock or use the button above
          </p>
        </form>
      </div>
    </div>
  );
}
